import { ApolloProvider } from "@apollo/client/react";
import isPropValid from "@emotion/is-prop-valid";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { LoginCallback, Security } from "@okta/okta-react";
import * as React from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { StyleSheetManager, ThemeProvider } from "styled-components";
import { SecureRoute } from "./components/SecureRoute";
import { createApolloClient } from "./lib/apollo";
import { oktaAuth } from "./lib/okta/okta";
import { OrdersViewRouter } from "./pages/orders/orders-view-router";
import { ORDERS_PATH, ROOT_PATH } from "./pages/page-routing-config";
import { RadarProvider } from "./reducers/reducers";
import { CFATheme, GlobalStyle } from "./theme";

const client = createApolloClient();

// styled-components v6 forwards every prop to the DOM by default; combined with
// styled-system this would leak style props (mt, px, variant, ...) onto DOM nodes.
// Restore v5 filtering: for DOM elements forward only valid HTML attributes; for
// composite components forward all props.
const shouldForwardProp = (propName: string, target: unknown) =>
  typeof target === "string" ? isPropValid(propName) : true;

function App() {
  const navigate = useNavigate();
  const restoreOriginalUri = async (_: unknown, originalUri: string) => {
    navigate(toRelativeUrl(originalUri || "/", window.location.origin), {
      replace: true,
    });
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <RadarProvider>
        <ApolloProvider client={client}>
          <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <ThemeProvider theme={CFATheme}>
              <GlobalStyle />
              <Routes>
                <Route
                  path={`${ORDERS_PATH}/*`}
                  element={
                    <SecureRoute>
                      <OrdersViewRouter />
                    </SecureRoute>
                  }
                />
                <Route
                  path="/implicit/callback"
                  element={<LoginCallback />}
                />
                <Route
                  path={ROOT_PATH}
                  element={
                    <SecureRoute>
                      <Navigate to={ORDERS_PATH} replace />
                    </SecureRoute>
                  }
                />
              </Routes>
            </ThemeProvider>
          </StyleSheetManager>
        </ApolloProvider>
      </RadarProvider>
    </Security>
  );
}

export default App;
